import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Mail, Lock, Loader2, Check, AlertCircle, ArrowLeft } from "lucide-react";
import api from "../api";
import Logo from "../components/Logo";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const inputCls =
  "w-full h-12 pl-11 pr-4 rounded-xl bg-secondary border border-border outline-none focus:border-brand-500/60 focus:ring-2 focus:ring-brand-500/20 transition-all";

const detailOf = (e) =>
  e?.response?.data?.detail ||
  (e?.response ? "Something went wrong." : "We could not reach Trellure. Check your connection.");

function Frame({ title, lead, children, footer }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md animate-float-up">
        <Logo size="md" />
        <h1 className="font-display text-3xl font-semibold tracking-tight">{title}</h1>
        <p className="text-muted-foreground mt-2 text-sm">{lead}</p>
        {children}
        <div className="mt-6 text-center text-sm">
          {footer ?? (
            <Link to="/login" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

/** Step one: ask for the address. */
export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await api.post("/auth/forgot-password", { email });
      setSent(res.data);
    } catch {
      // The endpoint answers the same way whether or not the address exists, so
      // there is nothing useful to distinguish here either.
      setSent({ delivery: "email" });
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <Frame
        title="Check your email"
        lead={`If ${email} has an account, a link to choose a new password is on its way. It works for one hour.`}
      >
        <div className="mt-6 flex items-start gap-3 rounded-xl border border-border bg-secondary/50 px-4 py-3.5">
          <Check className="w-4 h-4 text-brand-400 shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground">
            Nothing arrives after a few minutes? Look in spam, then{" "}
            <button onClick={() => setSent(null)} className="text-brand-400 hover:text-brand-300">
              try another address
            </button>.
          </p>
        </div>
        {sent.delivery === "unconfigured" && (
          <div className="mt-3 flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3.5">
            <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-200/90">
              Email delivery is not switched on for this store yet, so the link cannot be sent.
              Write to <a href="mailto:hello@trellure.com" className="underline">hello@trellure.com</a> and
              we will sort it out by hand.
            </p>
          </div>
        )}
      </Frame>
    );
  }

  return (
    <Frame
      title="Forgot your password?"
      lead="Enter the address you signed up with and we will send you a link to choose a new one."
    >
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div className="relative">
          <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <label htmlFor="fp-email" className="sr-only">Email</label>
          <input id="fp-email" required type="email" autoComplete="email" className={inputCls}
            value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" />
        </div>
        <Button type="submit" disabled={busy}
          className="w-full h-12 bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold rounded-xl text-base">
          {busy ? "Sending…" : "Send me a link"}
        </Button>
      </form>
    </Frame>
  );
}

/** Step two: the link from the email. */
export function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { adoptSession } = useAuth();
  const [state, setState] = useState("checking"); // checking | ready | invalid
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  React.useEffect(() => {
    api.get(`/auth/reset-password/${encodeURIComponent(token)}`)
      .then((r) => setState(r.data?.valid ? "ready" : "invalid"))
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const res = await api.post("/auth/reset-password", { token, password });
      // Straight in: making someone type the password they just chose is a step
      // that exists only to be annoying.
      adoptSession(res.data.token, res.data.user);
      navigate("/library", { replace: true });
    } catch (err) {
      setError(detailOf(err));
    } finally {
      setBusy(false);
    }
  };

  if (state === "checking") {
    return (
      <div className="min-h-screen flex items-center justify-center" role="status" aria-label="Checking the link">
        <Loader2 className="w-6 h-6 text-muted-foreground/60 animate-spin" />
      </div>
    );
  }

  if (state === "invalid") {
    return (
      <Frame
        title="This link has expired"
        lead="Reset links work for one hour, and only once. Ask for a fresh one and it will arrive in a moment."
        footer={<Link to="/login" className="text-muted-foreground hover:text-foreground">Back to sign in</Link>}
      >
        <Button onClick={() => navigate("/forgot-password")}
          className="mt-8 w-full h-12 bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold rounded-xl text-base">
          Send me a new link
        </Button>
      </Frame>
    );
  }

  return (
    <Frame title="Choose a new password" lead="Pick something you have not used elsewhere. At least 8 characters.">
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div className="relative">
          <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <label htmlFor="rp-pw" className="sr-only">New password</label>
          <input id="rp-pw" required type="password" autoComplete="new-password" className={inputCls}
            value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password" />
        </div>
        {error && <p className="text-sm text-red-400">{error}</p>}
        <Button type="submit" disabled={busy || password.length < 8}
          className="w-full h-12 bg-brand-500 hover:bg-brand-400 text-brand-950 font-semibold rounded-xl text-base">
          {busy ? "Saving…" : "Save and sign in"}
        </Button>
      </form>
    </Frame>
  );
}
